import { FC, memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { T_KEYS } from '../../constants';

interface IRefreshSyncConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  isLoading?: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.48);
  backdrop-filter: blur(2.6px);
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div<{ $isRtl?: boolean }>`
  background: white;
  border-radius: 20px;
  padding: 32px;
  width: 480px;
  max-width: 90%;
  box-shadow: 2px 4px 20px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease;
  direction: ${(props) => (props.$isRtl ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.$isRtl ? 'right' : 'left')};

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #262626;
  margin: 0;
`;

const Description = styled.p`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  height: 44px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary'; $isLoading?: boolean }>`
  flex: 1;
  height: 44px;
  border-radius: 8px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  cursor: ${(props) => (props.$isLoading ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.$isLoading ? 0.7 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${(props) => {
    if (props.$variant === 'secondary') {
      return `
        background: white;
        color: #1a1a1a;
        border: 1px solid #e4e4e7;
        
        &:hover {
          background: #f9fafb;
        }
      `;
    }
    return `
      background: #5b2ff9;
      color: white;
      border: none;
      
      &:hover {
        background: #4a2fe8;
      }
    `;
  }}
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RefreshSyncConfirmModal: FC<IRefreshSyncConfirmModalProps> = memo(({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer $isRtl={isRtl}>
        <ContentSection>
          <Title>{t(T_KEYS.REFRESH_SYNC_MODAL_TITLE)}</Title>
          <Description>{t(T_KEYS.REFRESH_SYNC_MODAL_DESCRIPTION)}</Description>
        </ContentSection>
        <ButtonsRow>
          <Button
            $variant="primary"
            onClick={async (e) => {
              e.stopPropagation();
              await onConfirm();
            }}
            disabled={isLoading}
            $isLoading={isLoading}
          >
            {isLoading && <Spinner />}
            {t(T_KEYS.REFRESH_SYNC_MODAL_CONFIRM)}
          </Button>
          <Button
            $variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {t(T_KEYS.REFRESH_SYNC_MODAL_CANCEL)}
          </Button>
        </ButtonsRow>
      </ModalContainer>
    </Overlay>
  );
});

RefreshSyncConfirmModal.displayName = 'RefreshSyncConfirmModal';

export default RefreshSyncConfirmModal;
